
import React from 'react';
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatCurrency, formatDate } from './utils/formatters';
import { FileTextIcon, CircleDollarSignIcon } from 'lucide-react';

interface RepaymentsTableProps {
  repayments: any[];
  onViewDetails: (repayment: any) => void;
  onProcessPayment?: (repayment: any) => void;
}

export const RepaymentsTable: React.FC<RepaymentsTableProps> = ({ 
  repayments,
  onViewDetails,
  onProcessPayment
}) => {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead>Due Date</TableHead>
            <TableHead>Borrowing</TableHead>
            <TableHead>Lender</TableHead>
            <TableHead className="text-right">Principal</TableHead>
            <TableHead className="text-right">Interest</TableHead>
            <TableHead className="text-right">Total Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {repayments.length > 0 ? (
            repayments.map((repayment) => (
              <TableRow key={repayment.id} className="group hover:bg-muted/70">
                <TableCell>
                  {formatDate(repayment.date)}
                  {repayment.daysToGo !== undefined && (
                    <div className="text-xs text-muted-foreground">
                      {repayment.daysToGo === 0 ? 'Today' : 
                      repayment.daysToGo === 1 ? 'Tomorrow' : 
                      repayment.daysToGo > 0 ? `In ${repayment.daysToGo} days` :
                      `${Math.abs(repayment.daysToGo)} days ago`}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{repayment.borrowing}</TableCell>
                <TableCell>{repayment.lender || 'HDFC Bank'}</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(repayment.principal || 0)}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(repayment.interest || 0)}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(repayment.amount)}
                </TableCell>
                <TableCell>
                  <Badge 
                    variant={
                      repayment.status === 'Paid' ? 'outline' : 
                      repayment.status === 'Upcoming' && repayment.daysToGo && repayment.daysToGo <= 7 ? 'default' : 
                      repayment.status === 'Overdue' ? 'destructive' :
                      'secondary'
                    }
                  >
                    {repayment.status}
                  </Badge>
                  {repayment.status === 'Paid' && repayment.paidDate && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Paid on {formatDate(repayment.paidDate)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => onViewDetails(repayment)}
                      title="View Details"
                    >
                      <FileTextIcon className="h-4 w-4" />
                    </Button>
                    {repayment.status !== 'Paid' && onProcessPayment && (
                      <Button 
                        size="sm"
                        variant={repayment.status === 'Overdue' ? 'destructive' : 'default'}
                        onClick={() => onProcessPayment(repayment)}
                      >
                        <CircleDollarSignIcon className="h-4 w-4 mr-2" />
                        Process Payment
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center">
                No repayments found matching your filters
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
